/**
 * Admin Wallet Component
 * ======================
 * Displays admin wallet balance, earnings, transactions, and withdrawal functionality.
 * All amounts in USDT (TRC20).
 */

import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Wallet,
  DollarSign,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Copy,
  CheckCircle,
  Clock,
  AlertTriangle,
  ExternalLink,
  Download,
  Send
} from "lucide-react";
import { toast } from "@/components/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format currency
const formatCurrency = (amount, decimals = 2) => {
  if (amount === null || amount === undefined) return "0.00";
  return Number(amount).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

// Wallet Stat Card
const WalletStatCard = ({ icon: Icon, label, value, subLabel, color = "#00F0FF", trend }) => (
  <div className="p-6 rounded-xl bg-neutral-100 dark:bg-neutral-900/50 border border-neutral-200 dark:border-white/10">
    <div className="flex items-center justify-between mb-4">
      <div 
        className="w-12 h-12 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: `${color}20` }}
      >
        <Icon size={24} style={{ color }} />
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 text-sm ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {trend >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
          <span>{Math.abs(trend)}%</span>
        </div>
      )}
    </div>
    <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">{label}</p>
    <p className="text-2xl font-bold text-neutral-800 dark:text-white">{value}</p>
    {subLabel && (
      <p className="text-xs text-neutral-400 mt-1">{subLabel}</p>
    )}
  </div>
);

// Transaction Row
const TransactionRow = ({ transaction, isRTL }) => {
  const isCredit = ["subscription", "commission"].includes(transaction.transaction_type);
  const statusColors = {
    completed: "text-green-400",
    pending: "text-yellow-400",
    failed: "text-red-400"
  };

  return (
    <div className="flex items-center justify-between p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          isCredit ? 'bg-green-500/20' : 'bg-red-500/20'
        }`}>
          {isCredit ? (
            <ArrowDownRight className="text-green-400" size={20} />
          ) : (
            <ArrowUpRight className="text-red-400" size={20} />
          )}
        </div>
        <div>
          <p className="font-medium text-neutral-800 dark:text-white">
            {transaction.description || transaction.transaction_type}
          </p>
          <p className="text-sm text-neutral-500">
            {formatDate(transaction.created_at)}
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className={`font-bold ${isCredit ? 'text-green-400' : 'text-red-400'}`}>
          {isCredit ? '+' : '-'}{formatCurrency(transaction.amount_usdt)} USDT
        </p>
        <p className={`text-sm ${statusColors[transaction.status] || 'text-neutral-400'}`}>
          {transaction.status}
        </p>
      </div>
    </div>
  );
};

export default function AdminWallet() {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  
  // State
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Withdrawal dialog
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawAddress, setWithdrawAddress] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);
  
  // Copy state
  const [copied, setCopied] = useState(false);
  
  // Admin wallet address
  const adminWalletAddress = "TKuQF2J7mSXmHbb7DqtarqfAhKFeyT7PAQ";

  // Fetch wallet data
  const fetchWalletData = async () => {
    try {
      const [walletRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/payments/admin/wallet`),
        axios.get(`${API}/payments/withdrawals/admin`)
      ]);
      
      setWallet(walletRes.data.wallet || {
        balance_usdt: 0,
        total_earnings_usdt: 0,
        total_withdrawn_usdt: 0
      });
      
      // Get transactions (mock for now, will be implemented)
      setTransactions([]);
      setWithdrawals(transactionsRes.data.withdrawals || []);
      
    } catch (error) {
      console.error("Error fetching wallet data:", error);
      // Set default values on error
      setWallet({
        balance_usdt: 0,
        total_earnings_usdt: 0,
        total_withdrawn_usdt: 0
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchWalletData();
  }, []);

  // Refresh data
  const handleRefresh = () => {
    setRefreshing(true);
    fetchWalletData();
  };

  // Copy address
  const handleCopyAddress = () => {
    navigator.clipboard.writeText(adminWalletAddress);
    setCopied(true);
    toast.success(isRTL ? "تم نسخ العنوان" : "Address copied");
    setTimeout(() => setCopied(false), 2000);
  };

  // Handle withdrawal
  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      toast.error(isRTL ? "أدخل مبلغ صحيح" : "Enter a valid amount");
      return;
    }
    
    if (!withdrawAddress || !withdrawAddress.startsWith("T")) {
      toast.error(isRTL ? "أدخل عنوان TRC20 صحيح" : "Enter a valid TRC20 address");
      return;
    }
    
    if (parseFloat(withdrawAmount) > (wallet?.balance_usdt || 0)) {
      toast.error(isRTL ? "الرصيد غير كافي" : "Insufficient balance");
      return;
    }

    setWithdrawing(true);
    
    try {
      await axios.post(`${API}/payments/withdraw`, {
        user_id: "admin",
        amount: parseFloat(withdrawAmount),
        wallet_address: withdrawAddress,
        wallet_type: "admin"
      });
      
      toast.success(isRTL ? "تم إرسال طلب السحب" : "Withdrawal request submitted");
      setShowWithdrawDialog(false);
      setWithdrawAmount("");
      setWithdrawAddress("");
      fetchWalletData();
      
    } catch (error) {
      console.error("Withdrawal error:", error);
      toast.error(error.response?.data?.detail || (isRTL ? "فشل طلب السحب" : "Withdrawal failed"));
    } finally {
      setWithdrawing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-violet-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-neutral-800 dark:text-white">
            {isRTL ? "محفظة المنصة" : "Platform Wallet"}
          </h2>
          <p className="text-neutral-500 dark:text-neutral-400">
            {isRTL ? "إدارة إيرادات وسحب أموال المنصة" : "Manage platform earnings and withdrawals"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-2"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            {isRTL ? "تحديث" : "Refresh"}
          </Button>
          <Button
            onClick={() => setShowWithdrawDialog(true)}
            className="gap-2 bg-violet-600 hover:bg-violet-700"
          >
            <Send size={16} />
            {isRTL ? "سحب" : "Withdraw"}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <WalletStatCard
          icon={Wallet}
          label={isRTL ? "الرصيد الحالي" : "Current Balance"}
          value={`${formatCurrency(wallet?.balance_usdt)} USDT`}
          subLabel="TRC20"
          color="#00F0FF"
        />
        <WalletStatCard
          icon={TrendingUp}
          label={isRTL ? "إجمالي الإيرادات" : "Total Earnings"}
          value={`${formatCurrency(wallet?.total_earnings_usdt)} USDT`}
          subLabel={isRTL ? "منذ البداية" : "All time"}
          color="#10B981"
        />
        <WalletStatCard
          icon={ArrowUpRight}
          label={isRTL ? "إجمالي المسحوب" : "Total Withdrawn"}
          value={`${formatCurrency(wallet?.total_withdrawn_usdt)} USDT`}
          subLabel={isRTL ? "منذ البداية" : "All time"}
          color="#F59E0B"
        />
      </div>

      {/* Wallet Address Card */}
      <div className="p-6 rounded-xl bg-gradient-to-br from-violet-600/20 to-purple-600/20 border border-violet-500/30">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
              <Wallet className="text-violet-400" size={20} />
            </div>
            <div>
              <p className="text-sm text-neutral-400">
                {isRTL ? "عنوان الاستلام (USDT TRC20)" : "Receiving Address (USDT TRC20)"}
              </p>
              <p className="font-mono text-neutral-800 dark:text-white break-all">
                {adminWalletAddress}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopyAddress}
            className="gap-2"
          >
            {copied ? <CheckCircle size={16} className="text-green-400" /> : <Copy size={16} />}
            {copied ? (isRTL ? "تم النسخ" : "Copied") : (isRTL ? "نسخ" : "Copy")}
          </Button>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="rounded-xl bg-neutral-100 dark:bg-neutral-900/50 border border-neutral-200 dark:border-white/10 overflow-hidden">
        <div className="p-4 border-b border-neutral-200 dark:border-white/10">
          <h3 className="font-semibold text-neutral-800 dark:text-white">
            {isRTL ? "آخر المعاملات" : "Recent Transactions"}
          </h3>
        </div>
        <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
          {transactions.length > 0 ? (
            transactions.map((tx, index) => (
              <TransactionRow key={tx.id || index} transaction={tx} isRTL={isRTL} />
            ))
          ) : (
            <div className="text-center py-8 text-neutral-500">
              <Wallet size={48} className="mx-auto mb-4 opacity-50" />
              <p>{isRTL ? "لا توجد معاملات بعد" : "No transactions yet"}</p>
              <p className="text-sm">
                {isRTL 
                  ? "ستظهر المعاملات هنا عند استلام مدفوعات" 
                  : "Transactions will appear here when payments are received"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Withdrawal History */}
      {withdrawals.length > 0 && (
        <div className="rounded-xl bg-neutral-100 dark:bg-neutral-900/50 border border-neutral-200 dark:border-white/10 overflow-hidden">
          <div className="p-4 border-b border-neutral-200 dark:border-white/10">
            <h3 className="font-semibold text-neutral-800 dark:text-white">
              {isRTL ? "سجل السحوبات" : "Withdrawal History"}
            </h3>
          </div>
          <div className="p-4 space-y-3 max-h-64 overflow-y-auto">
            {withdrawals.map((withdrawal, index) => (
              <div 
                key={withdrawal.id || index}
                className="flex items-center justify-between p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800/50"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <ArrowUpRight className="text-orange-400" size={20} />
                  </div>
                  <div>
                    <p className="font-mono text-sm text-neutral-600 dark:text-neutral-300">
                      {withdrawal.wallet_address?.slice(0, 10)}...{withdrawal.wallet_address?.slice(-8)}
                    </p>
                    <p className="text-sm text-neutral-500">
                      {formatDate(withdrawal.created_at)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-orange-400">
                    -{formatCurrency(withdrawal.amount_usdt)} USDT
                  </p>
                  <p className={`text-sm ${
                    withdrawal.status === 'completed' ? 'text-green-400' :
                    withdrawal.status === 'pending' ? 'text-yellow-400' :
                    withdrawal.status === 'processing' ? 'text-blue-400' :
                    'text-red-400'
                  }`}>
                    {withdrawal.status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Withdrawal Dialog */}
      <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isRTL ? "سحب إلى محفظة خارجية" : "Withdraw to External Wallet"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Available Balance */}
            <div className="p-4 rounded-lg bg-neutral-100 dark:bg-neutral-800">
              <p className="text-sm text-neutral-500 mb-1">
                {isRTL ? "الرصيد المتاح" : "Available Balance"}
              </p>
              <p className="text-2xl font-bold text-neutral-800 dark:text-white">
                {formatCurrency(wallet?.balance_usdt)} USDT
              </p>
            </div>
            
            {/* Amount Input */}
            <div>
              <label className="text-sm text-neutral-500 mb-2 block">
                {isRTL ? "المبلغ (USDT)" : "Amount (USDT)"}
              </label>
              <div className="relative">
                <Input
                  type="number"
                  placeholder="0.00"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  className="pr-16"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-xs"
                  onClick={() => setWithdrawAmount(wallet?.balance_usdt?.toString() || "0")}
                >
                  {isRTL ? "الكل" : "MAX"}
                </Button>
              </div>
            </div>
            
            {/* Address Input */}
            <div>
              <label className="text-sm text-neutral-500 mb-2 block">
                {isRTL ? "عنوان المحفظة (TRC20)" : "Wallet Address (TRC20)"}
              </label>
              <Input
                placeholder="TXxx..."
                value={withdrawAddress}
                onChange={(e) => setWithdrawAddress(e.target.value)}
              />
              <p className="text-xs text-yellow-500 mt-1">
                {isRTL 
                  ? "⚠️ تأكد من أن العنوان على شبكة TRON (TRC20)" 
                  : "⚠️ Make sure the address is on TRON network (TRC20)"}
              </p>
            </div>

            {/* Info */}
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <p className="text-sm text-green-400">
                {isRTL 
                  ? "✓ لا يوجد حد أدنى أو أقصى للسحب من محفظة المشرف" 
                  : "✓ No minimum or maximum limit for admin withdrawals"}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowWithdrawDialog(false)}
            >
              {isRTL ? "إلغاء" : "Cancel"}
            </Button>
            <Button
              onClick={handleWithdraw}
              disabled={withdrawing || !withdrawAmount || !withdrawAddress}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {withdrawing ? (
                <RefreshCw className="animate-spin mr-2" size={16} />
              ) : (
                <Send className="mr-2" size={16} />
              )}
              {isRTL ? "سحب" : "Withdraw"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
